import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Package, Boxes, Factory, TrendingUp, History, Edit2, Plus, Trash2, AlertTriangle, Truck, ChevronDown, ChevronUp, FlaskConical } from 'lucide-react';
import { costingAPI } from '../utils/api';
import Modal from '../components/common/Modal';
import SectionHero from '../components/common/SectionHero';
import { localName, fmt, money, apiError } from '../utils/helpers';

const SPAN = 90;   // the cover bar shows the next 90 days
const STATUS_COLOR = { ok: '#34d399', low: '#fbbf24', critical: '#f87171', gap: '#f87171', idle: '#64748b' };
const usd = (v, d = 2) => (v === null || v === undefined ? '—' : `${v < 0 ? '−' : ''}$${fmt(Math.abs(v), d)}`);
const unitCost = (v) => (v === null || v === undefined ? '—' : `$${fmt(v, v < 1 ? 4 : 2)}`);
const daysFrom = (iso) => (iso ? Math.round((new Date(iso) - new Date(new Date().toISOString().slice(0, 10))) / 86400000) : null);

/* stock cover as a 90-day runway: the bar is how long the stock lasts, each tick a load on the road */
function CoverBar({ m, t }) {
  if (m.status === 'idle') return <span className="cs-idle">{t('cs_no_usage')}</span>;
  const pct = Math.min(100, (m.cover_days / SPAN) * 100);
  return (
    <div className="cs-cover" style={{ '--c': STATUS_COLOR[m.status] }}>
      <div className="cs-track">
        <i className="cs-fill" style={{ width: `${pct}%` }} />
        {(m.arrivals || []).map(a => {
          const d = daysFrom(a.eta);
          if (d === null || d < 0 || d > SPAN) return null;
          return <b key={a.shipment_id} className="cs-eta" style={{ insetInlineStart: `${(d / SPAN) * 100}%` }}
                    title={`${a.shipment_number} · ${a.eta} · +${fmt(a.quantity)} ${m.unit}`}><Truck size={10} /></b>;
        })}
      </div>
      <span className="cs-days"><bdi>{m.cover_days > 999 ? '999+' : fmt(m.cover_days, 0)}</bdi> {t('cs_days')}</span>
    </div>
  );
}

function Summary({ s, t }) {
  const cells = [
    [t('cs_raw_value'), money(s.raw_value_usd)],
    [t('cs_products_value'), money(s.products_value_usd)],
    [t('cs_at_risk'), s.at_risk, s.at_risk ? '#f87171' : null],
    [t('cs_missing_cost'), s.missing_cost, s.missing_cost ? '#fbbf24' : null],
  ];
  return <div className="cs-sum">{cells.map(([l, v, c]) => <div key={l}><span>{l}</span><strong style={c ? { color: c } : null}><bdi>{v}</bdi></strong></div>)}</div>;
}

function Materials({ data, lang, t, reload }) {
  const [hist, setHist] = useState(null);
  const [edit, setEdit] = useState(null);
  const open = async (m) => { try { setHist({ m, ...(await costingAPI.history(m.id)).data }); } catch (e) { toast.error(t('error')); } };
  const save = async () => {
    const v = edit.value === '' ? null : parseFloat(edit.value);
    if (v !== null && (isNaN(v) || v < 0)) return toast.error(t('fill_required'));
    try { await costingAPI.setMaterialCost(edit.m.id, { standard_cost_usd: v }); toast.success(t('saved')); setEdit(null); reload(); }
    catch (e) { toast.error(apiError(e, t)); }
  };
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="table-wrap"><table className="cs-table cs-mtable">
        <thead><tr><th>{t('raw_materials')}</th><th>{t('cs_stock')}</th><th>{t('cs_unit_cost')}</th><th className="hide-mobile">{t('cs_value')}</th>
          <th className="hide-mobile">{t('cs_per_day')}</th><th>{t('cs_cover')}</th><th>{t('actions')}</th></tr></thead>
        <tbody>{data.map(m => (
          <tr key={m.id}>
            <td><div className="cs-name">{localName(m, lang)}</div>
              <div className={`cs-state s-${m.status}`}>{t('cs_st_' + m.status)}{m.status === 'gap' && m.gap_days ? ` · ${m.gap_days} ${t('cs_days')}` : ''}</div></td>
            <td><bdi className="cs-num">{fmt(m.quantity)}</bdi> <span className="cs-unit">{m.unit}</span>
              {m.on_road > 0 && <Link to="/shipments" className="transit-chip"><Truck size={11} /> +<bdi>{fmt(m.on_road)}</bdi></Link>}</td>
            <td data-l={t('cs_unit_cost')}><bdi className="cs-num">{unitCost(m.avg_cost_usd)}</bdi>
              {m.cost_source === 'manual' && <span className="cs-tag">{t('cs_manual')}</span>}
              {m.unpriced > 0 && <span className="cs-tag warn" title={t('cs_unpriced_hint')}><AlertTriangle size={10} /> {m.unpriced}</span>}</td>
            <td className="hide-mobile"><bdi>{usd(m.value_usd, 0)}</bdi></td>
            <td className="hide-mobile">{m.per_day ? <><bdi>{fmt(m.per_day, m.per_day < 10 ? 1 : 0)}</bdi> <span className="cs-unit">{m.unit}</span></> : '—'}</td>
            <td><CoverBar m={m} t={t} />{m.suggest_order > 0 && m.status !== 'ok' && <div className="cs-suggest">{t('cs_suggest')}: <bdi>{fmt(m.suggest_order, 0)} {m.unit}</bdi></div>}</td>
            <td><div className="flex gap-1">
              <button className="btn btn-ghost btn-sm btn-icon" title={t('cs_history')} onClick={() => open(m)}><History size={14} /></button>
              <button className="btn btn-ghost btn-sm btn-icon" title={t('cs_manual_cost')} onClick={() => setEdit({ m, value: m.standard_cost_usd ?? '' })}><Edit2 size={14} /></button>
            </div></td>
          </tr>
        ))}</tbody>
      </table></div>
      <p className="cs-note">{t('cs_cover_note')}</p>

      {hist && (
        <Modal title={`${t('cs_history')} — ${localName(hist.m, lang)}`} size="modal-lg" onClose={() => setHist(null)}>
          <div className="cs-hist-head"><span>{t('cs_unit_cost')}</span><strong><bdi>{unitCost(hist.avg_cost_usd)}</bdi></strong><span className="cs-unit">/ {hist.m.unit}</span></div>
          {hist.unpriced.length > 0 && <div className="cs-alert"><AlertTriangle size={14} /> {t('cs_unpriced_hint')} {hist.unpriced.map(u => <Link key={u.ref_id} to={`/shipments?open=${u.ref_id}`}>{u.ref}</Link>)}</div>}
          {hist.rows.length === 0 ? <p className="sh-empty">{t('cs_no_inflows')}</p> : (
            <div className="table-wrap"><table>
              <thead><tr><th>{t('date')}</th><th>{t('cs_source')}</th><th>{t('quantity')}</th><th>{t('cs_goods')}</th><th>{t('cs_freight_customs')}</th><th>{t('cs_unit_cost')}</th><th>{t('cs_avg_after')}</th></tr></thead>
              <tbody>{hist.rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.date}</td>
                  <td>{t('cs_src_' + r.kind)}{r.ref && <div className="text-xs">{r.kind === 'shipment' ? <Link to={`/shipments?open=${r.ref_id}`}><bdi>{r.ref}</bdi></Link> : <bdi>{r.ref}</bdi>}</div>}</td>
                  <td><bdi>{fmt(r.quantity)}</bdi></td>
                  <td><bdi>{usd(r.goods_usd ?? r.value_usd)}</bdi></td>
                  <td><bdi>{r.extra_usd ? usd(r.extra_usd) : '—'}</bdi></td>
                  <td><bdi style={{ fontWeight: 700 }}>{unitCost(r.unit_cost_usd)}</bdi></td>
                  <td><bdi>{unitCost(r.avg_after_usd)}</bdi></td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </Modal>
      )}
      {edit && (
        <Modal title={`${t('cs_manual_cost')} — ${localName(edit.m, lang)}`} onClose={() => setEdit(null)}
          footer={<><button className="btn btn-secondary" onClick={() => setEdit(null)}>{t('cancel')}</button><button className="btn btn-primary" onClick={save}>{t('save')}</button></>}>
          <p className="text-xs" style={{ color: 'var(--text-muted)', marginBottom: 12 }}>{t('cs_manual_hint')}</p>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">USD / {edit.m.unit}</label>
            <input className="input" type="number" min="0" step="any" autoFocus value={edit.value} onChange={e => setEdit(x => ({ ...x, value: e.target.value }))} /></div>
        </Modal>
      )}
    </div>
  );
}

function RecipeModal({ p, materials, lang, t, onClose, onSaved }) {
  const [lines, setLines] = useState(p.recipe.length ? p.recipe.map(r => ({ raw_material_id: r.raw_material_id, qty_per_roll: r.qty_per_roll })) : [{ raw_material_id: '', qty_per_roll: '' }]);
  const [oh, setOh] = useState(p.overhead_per_roll_usd || '');
  const mats = useMemo(() => Object.fromEntries(materials.map(m => [m.id, m])), [materials]);
  const preview = lines.reduce((s, l) => s + (parseFloat(l.qty_per_roll) || 0) * (mats[l.raw_material_id]?.avg_cost_usd || 0), 0) + (parseFloat(oh) || 0);
  const save = async () => {
    const clean = lines.filter(l => l.raw_material_id && parseFloat(l.qty_per_roll) > 0).map(l => ({ raw_material_id: l.raw_material_id, qty_per_roll: parseFloat(l.qty_per_roll) }));
    try { await costingAPI.setRecipe(p.id, { overhead_per_roll_usd: parseFloat(oh) || 0, lines: clean }); toast.success(t('saved')); onSaved(); }
    catch (e) { toast.error(apiError(e, t)); }
  };
  const set = (i, k, v) => setLines(ls => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  return (
    <Modal title={`${t('cs_recipe')} — ${localName(p, lang)}`} size="modal-lg" onClose={onClose}
      footer={<><span className="cs-preview">{t('cs_std_cost')}: <bdi>{unitCost(preview)}</bdi> / {t('cs_roll')}</span>
        <button className="btn btn-secondary" onClick={onClose}>{t('cancel')}</button><button className="btn btn-primary" onClick={save}>{t('save')}</button></>}>
      <p className="text-xs" style={{ color: 'var(--text-muted)', marginBottom: 12 }}>{t('cs_recipe_hint')}</p>
      {lines.map((l, i) => {
        const m = mats[l.raw_material_id];
        return (
          <div key={i} className="cs-rline">
            <select className="select" value={l.raw_material_id} onChange={e => set(i, 'raw_material_id', e.target.value)}>
              <option value="">{t('cs_pick')}</option>
              {materials.map(mm => <option key={mm.id} value={mm.id} disabled={mm.id !== l.raw_material_id && lines.some(x => x.raw_material_id === mm.id)}>{localName(mm, lang)}</option>)}
            </select>
            <div className="cs-qty"><input className="input" type="number" min="0" step="any" placeholder="0" value={l.qty_per_roll} onChange={e => set(i, 'qty_per_roll', e.target.value)} />
              <span>{m ? m.unit : ''} / {t('cs_roll')}</span></div>
            <bdi className="cs-rcost">{m && l.qty_per_roll ? unitCost((parseFloat(l.qty_per_roll) || 0) * (m.avg_cost_usd || 0)) : ''}</bdi>
            <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setLines(ls => ls.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
          </div>
        );
      })}
      <button className="btn btn-ghost btn-sm" onClick={() => setLines(ls => [...ls, { raw_material_id: '', qty_per_roll: '' }])}><Plus size={14} /> {t('cs_add_line')}</button>
      <div className="form-group" style={{ marginTop: 16, marginBottom: 0 }}>
        <label className="form-label">{t('cs_overhead')} (USD / {t('cs_roll')})</label>
        <input className="input" type="number" min="0" step="any" value={oh} onChange={e => setOh(e.target.value)} style={{ maxWidth: 200 }} />
        <p className="text-xs" style={{ color: 'var(--text-muted)', marginTop: 6 }}>{t('cs_overhead_hint')}</p>
      </div>
    </Modal>
  );
}

function ProductCard({ p, matsById, lang, t, canProfit, onRecipe }) {
  const [plan, setPlan] = useState('');
  const n = parseFloat(plan) || 0;
  const delta = p.avg_cost_usd != null && p.standard_cost_usd != null ? (p.avg_cost_usd - p.standard_cost_usd) / p.standard_cost_usd * 100 : null;
  return (
    <div className="cs-card">
      <div className="cs-card-top">
        <h3>{localName(p, lang)}</h3>
        <button className="btn btn-ghost btn-sm" onClick={onRecipe}><FlaskConical size={14} /> {p.recipe.length ? t('cs_recipe') : t('cs_set_recipe')}</button>
      </div>
      <div className="cs-figs">
        <div className="big"><span>{t('cs_actual_cost')}</span><strong><bdi>{unitCost(p.avg_cost_usd)}</bdi></strong><em>/ {t('cs_roll')}</em></div>
        <div><span>{t('cs_std_cost')}</span><strong><bdi>{unitCost(p.standard_cost_usd)}</bdi></strong>
          {delta !== null && Math.abs(delta) >= 0.5 && <em className={delta > 0 ? 'up' : 'down'}><bdi>{delta > 0 ? '+' : '−'}{fmt(Math.abs(delta), 1)}%</bdi></em>}</div>
        <div><span>{t('cs_last_batch')}</span><strong><bdi>{unitCost(p.last_batch_cost_usd)}</bdi></strong></div>
        {canProfit && <div><span>{t('cs_margin')}</span><strong style={{ color: p.margin_pct == null ? null : p.margin_pct >= 0 ? '#34d399' : '#f87171' }}><bdi>{p.margin_pct == null ? '—' : `${fmt(p.margin_pct, 1)}%`}</bdi></strong>
          {p.avg_price_usd != null && <em><bdi>{t('cs_price')} {unitCost(p.avg_price_usd)}</bdi></em>}</div>}
      </div>
      {p.recipe.length > 0 ? (
        <>
          <div className="cs-bom">
            {p.recipe.map(r => {
              const share = p.standard_cost_usd ? (r.cost_usd || 0) / p.standard_cost_usd * 100 : 0;
              return (
                <div key={r.raw_material_id} className="cs-bom-row">
                  <span className="n">{localName(matsById[r.raw_material_id] || {}, lang)}</span>
                  <span className="q"><bdi>{fmt(r.qty_per_roll)}</bdi> {matsById[r.raw_material_id]?.unit}</span>
                  <span className="bar"><i style={{ width: `${share}%` }} /></span>
                  <bdi className="c">{unitCost(r.cost_usd)}</bdi>
                </div>
              );
            })}
            {p.overhead_per_roll_usd > 0 && <div className="cs-bom-row oh"><span className="n">{t('cs_overhead_short')}</span><span className="q" /><span className="bar"><i style={{ width: `${p.standard_cost_usd ? p.overhead_per_roll_usd / p.standard_cost_usd * 100 : 0}%` }} /></span><bdi className="c">{unitCost(p.overhead_per_roll_usd)}</bdi></div>}
          </div>
          <div className="cs-plan">
            <span>{t('cs_can_make')}: <strong><bdi>{fmt(p.possible_rolls, 0)}</bdi></strong> {t('cs_rolls')}{p.possible_with_road > p.possible_rolls && <> · <Truck size={11} /> <bdi>{fmt(p.possible_with_road, 0)}</bdi></>}</span>
            <label>{t('cs_plan')} <input className="input" type="number" min="0" value={plan} onChange={e => setPlan(e.target.value)} placeholder="0" /> {t('cs_rolls')}</label>
          </div>
          {n > 0 && (
            <div className="cs-need">{p.recipe.map(r => {
              const m = matsById[r.raw_material_id] || {};
              const need = r.qty_per_roll * n, have = m.quantity || 0, short = need - have;
              return <div key={r.raw_material_id} className={short > 0 ? 'short' : ''}><span>{localName(m, lang)}</span>
                <bdi>{fmt(need)} {m.unit}</bdi><b>{short > 0 ? <>{t('cs_short')} <bdi>{fmt(short)}</bdi></> : t('cs_enough')}</b></div>;
            })}<div className="tot"><span>{t('cs_plan_cost')}</span><bdi>{usd((p.standard_cost_usd || 0) * n)}</bdi></div></div>
          )}
        </>
      ) : <p className="cs-empty-recipe">{t('cs_no_recipe')}</p>}
      <div className="cs-card-foot"><span>{t('cs_stock')}: <bdi>{fmt(p.stock_rolls, 0)}</bdi> {t('cs_rolls')}</span><span>{t('cs_value')}: <bdi>{usd(p.value_usd, 0)}</bdi></span></div>
    </div>
  );
}

function Batches({ lang, t }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { costingAPI.batches().then(r => setRows(r.data)).catch(() => { toast.error(t('error')); setRows([]); }); }, []); // eslint-disable-line
  if (!rows) return <div className="loading-center"><div className="spinner" /></div>;
  if (!rows.length) return <div className="card empty-state"><Factory size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} /><p>{t('no_data')}</p></div>;
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="table-wrap"><table className="cs-table">
        <thead><tr><th>{t('batch_number')}</th><th>{t('date')}</th><th>{t('cs_rolls')}</th><th className="hide-mobile">{t('cs_materials_cost')}</th>
          <th className="hide-mobile">{t('cs_overhead_short')}</th><th>{t('cs_total')}</th><th>{t('cs_per_roll')}</th><th>{t('cs_variance')}</th><th /></tr></thead>
        <tbody>{rows.map(b => (
          <React.Fragment key={b.id}>
            <tr className="cs-click" onClick={() => setOpen(open === b.id ? null : b.id)}>
              <td><bdi className="cs-name">{b.batch_number}</bdi>{!b.complete && <div className="cs-tag warn" title={b.missing.join(', ')}><AlertTriangle size={10} /> {t('cs_incomplete')}</div>}</td>
              <td>{b.production_date}</td><td><bdi>{fmt(b.rolls, 0)}</bdi></td>
              <td className="hide-mobile"><bdi>{usd(b.material_usd)}</bdi></td>
              <td className="hide-mobile"><bdi>{usd((b.overhead_usd || 0) + (b.extra_usd || 0))}</bdi></td>
              <td><bdi style={{ fontWeight: 700 }}>{usd(b.total_usd)}</bdi></td>
              <td><bdi className="cs-num">{unitCost(b.per_roll_usd)}</bdi></td>
              <td>{b.variance_usd == null ? <span className="cs-unit">{t('cs_no_recipe_short')}</span> :
                <bdi className={`cs-var ${b.variance_usd > 0.005 ? 'up' : b.variance_usd < -0.005 ? 'down' : ''}`}>{b.variance_usd > 0 ? '+' : b.variance_usd < 0 ? '−' : ''}{money(Math.abs(b.variance_usd))}</bdi>}</td>
              <td>{open === b.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</td>
            </tr>
            {open === b.id && (
              <tr className="cs-detail"><td colSpan={9}>
                <div className="cs-detail-grid">
                  <div><h4>{t('cs_inputs')}</h4>{b.inputs.map((i, k) => (
                    <div key={k} className="cs-line"><span>{localName(i, lang)}</span><bdi>{fmt(i.quantity)} {i.unit} × {unitCost(i.unit_cost_usd)}</bdi><bdi>{usd(i.cost_usd)}</bdi></div>))}
                    {b.extra_usd > 0 && <div className="cs-line"><span>{t('cs_extra')}</span><span /><bdi>{usd(b.extra_usd)}</bdi></div>}</div>
                  <div><h4>{t('cs_outputs')} <span className="cs-unit">({t('cs_split_' + b.basis)})</span></h4>{b.outputs.map((o, k) => (
                    <div key={k} className="cs-line"><span>{localName(o, lang)}</span><bdi>{fmt(o.rolls, 0)} × {unitCost(o.per_roll_usd)}</bdi><bdi>{usd(o.cost_usd)}</bdi></div>))}</div>
                  {b.variance.length > 0 && <div className="wide"><h4>{t('cs_vs_recipe')}</h4>{b.variance.map((v, k) => (
                    <div key={k} className="cs-line"><span>{localName(v, lang)}</span>
                      <bdi>{t('cs_expected')} {fmt(v.expected)} · {t('cs_used')} {fmt(v.actual)} {v.unit}</bdi>
                      <bdi className={`cs-var ${v.diff > 0.0001 ? 'up' : v.diff < -0.0001 ? 'down' : ''}`}>{v.diff_pct == null ? '' : `${v.diff > 0 ? '+' : ''}${fmt(v.diff_pct, 1)}%`} {v.diff_usd != null && `(${v.diff_usd >= 0 ? '+' : '−'}${money(Math.abs(v.diff_usd))})`}</bdi></div>))}</div>}
                </div>
              </td></tr>
            )}
          </React.Fragment>
        ))}</tbody>
      </table></div>
    </div>
  );
}

function Profit({ lang, t }) {
  const y = new Date().getFullYear();
  const [range, setRange] = useState({ date_from: `${y}-01-01`, date_to: new Date().toISOString().slice(0, 10) });
  const [by, setBy] = useState('product');
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); costingAPI.profit(range).then(r => setD(r.data)).catch(() => toast.error(t('error'))); }, [range]); // eslint-disable-line
  const rows = d ? d['by_' + by] : [];
  const max = Math.max(1, ...rows.map(r => Math.abs(r.revenue_usd || 0)));
  const label = (r) => by === 'customer' ? (r.name || t('cs_walk_in')) : by === 'month' ? r.key : localName(r, lang);
  return (
    <div className="space-y-4">
      <div className="cs-range">
        <label>{t('cs_from')} <input className="input" type="date" value={range.date_from} onChange={e => setRange(x => ({ ...x, date_from: e.target.value }))} /></label>
        <label>{t('cs_to')} <input className="input" type="date" value={range.date_to} onChange={e => setRange(x => ({ ...x, date_to: e.target.value }))} /></label>
        <div className="mg-cats">{['product', 'customer', 'month'].map(k => <button key={k} className={by === k ? 'on' : ''} onClick={() => setBy(k)}>{t('cs_by_' + k)}</button>)}</div>
      </div>
      {!d ? <div className="loading-center"><div className="spinner" /></div> : (
        <>
          <div className="cs-sum">
            <div><span>{t('cs_revenue')}</span><strong><bdi>{money(d.totals.revenue_usd)}</bdi></strong></div>
            <div><span>{t('cs_cogs')}</span><strong><bdi>{money(d.totals.cost_usd)}</bdi></strong></div>
            <div><span>{t('cs_gross')}</span><strong style={{ color: d.totals.margin_usd >= 0 ? '#34d399' : '#f87171' }}><bdi>{money(d.totals.margin_usd)}</bdi></strong></div>
            <div><span>{t('cs_margin')}</span><strong><bdi>{d.totals.margin_pct == null ? '—' : `${fmt(d.totals.margin_pct, 1)}%`}</bdi></strong></div>
          </div>
          {d.totals.uncosted_usd > 0 && <div className="cs-alert"><AlertTriangle size={14} /> {t('cs_uncosted')}: <bdi>{money(d.totals.uncosted_usd)}</bdi></div>}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {rows.length === 0 ? <div className="empty-state"><p>{t('no_data')}</p></div> : (
              <div className="table-wrap"><table className="cs-table">
                <thead><tr><th>{t('cs_by_' + by)}</th><th className="hide-mobile">{t('cs_rolls')}</th><th>{t('cs_revenue')}</th><th className="hide-mobile">{t('cs_cogs')}</th><th>{t('cs_gross')}</th><th>{t('cs_margin')}</th></tr></thead>
                <tbody>{rows.map(r => (
                  <tr key={r.key}>
                    <td><div className="cs-name">{label(r)}</div><div className="cs-rev"><i style={{ width: `${(r.revenue_usd / max) * 100}%` }}><u style={{ width: `${r.revenue_usd ? Math.max(0, Math.min(100, (r.cost_usd / r.revenue_usd) * 100)) : 0}%` }} /></i></div></td>
                    <td className="hide-mobile"><bdi>{fmt(r.rolls, 0)}</bdi></td>
                    <td><bdi>{money(r.revenue_usd)}</bdi></td>
                    <td className="hide-mobile"><bdi>{money(r.cost_usd)}</bdi></td>
                    <td><bdi style={{ fontWeight: 700, color: r.margin_usd >= 0 ? '#34d399' : '#f87171' }}>{money(r.margin_usd)}</bdi></td>
                    <td><bdi>{r.margin_pct == null ? '—' : `${fmt(r.margin_pct, 1)}%`}</bdi></td>
                  </tr>
                ))}</tbody>
              </table></div>
            )}
          </div>
          <p className="cs-note">{t('cs_profit_note')}</p>
        </>
      )}
    </div>
  );
}

/** v3.8: real production overhead per roll (expenses of the production cost centre / rolls made) vs the recipe overhead. */
function OverheadCard({ rows, t }) {
  const shown = rows.filter(r => r.rolls > 0 || r.expenses_usd > 0).slice(-6);
  if (!shown.length) return null;
  return (
    <div className="card cs-oh">
      <div className="cs-oh-head"><b>{t('cs_oh_title')}</b><span>{t('cs_oh_hint')}</span></div>
      <div className="table-wrap"><table>
        <thead><tr><th>{t('cs_oh_month')}</th><th>{t('cs_oh_expenses')}</th><th>{t('cs_oh_rolls')}</th><th>{t('cs_oh_actual')}</th><th>{t('cs_oh_assumed')}</th></tr></thead>
        <tbody>{shown.map(r => {
          const diff = r.actual_per_roll_usd != null && r.assumed_per_roll_usd != null ? r.actual_per_roll_usd - r.assumed_per_roll_usd : null;
          return (
            <tr key={r.month}><td>{r.month}</td><td><bdi>{money(r.expenses_usd)}</bdi></td><td>{fmt(r.rolls)}</td>
              <td style={{ fontWeight: 700 }}>{r.actual_per_roll_usd != null ? <bdi>{money(r.actual_per_roll_usd)}</bdi> : '—'}</td>
              <td>{r.assumed_per_roll_usd != null ? <bdi>{money(r.assumed_per_roll_usd)}</bdi> : '—'}
                {diff != null && Math.abs(diff) > 0.005 && <span className="text-xs" style={{ color: diff > 0 ? '#f87171' : '#34d399', marginInlineStart: 6 }}>{diff > 0 ? '▲' : '▼'} <bdi>{money(Math.abs(diff))}</bdi></span>}</td></tr>
          );
        })}</tbody>
      </table></div>
    </div>
  );
}

export default function Costing() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [tab, setTab] = useState(() => { try { return localStorage.getItem('cs_tab') || 'materials'; } catch (e) { return 'materials'; } });
  const [d, setD] = useState(null);
  const [recipe, setRecipe] = useState(null);
  const load = () => costingAPI.overview().then(r => setD(r.data)).catch(e => { toast.error(apiError(e, t)); setD({ materials: [], products: [], summary: {}, error: true }); });
  useEffect(() => { load(); }, []); // eslint-disable-line
  const go = (k) => { setTab(k); try { localStorage.setItem('cs_tab', k); } catch (e) { /* ignore */ } };
  const matsById = useMemo(() => Object.fromEntries((d?.materials || []).map(m => [m.id, m])), [d]);
  const tabs = [['materials', Package, 'cs_tab_materials'], ['products', Boxes, 'cs_tab_products'], ['batches', Factory, 'cs_tab_batches'],
    ...(d?.can_see_profit ? [['profit', TrendingUp, 'cs_tab_profit']] : [])];
  const cur = tabs.some(x => x[0] === tab) ? tab : 'materials';
  return (
    <div className="space-y-5">
      <SectionHero title={t('costing')} subtitle={d && !d.error ? <>{d.materials.length} {t('raw_materials')} · {d.products.length} {t('cs_products_count')}</> : null} />
      {!d ? <div className="loading-center"><div className="spinner" /></div> : (
        <>
          {!d.error && <Summary s={d.summary} t={t} />}
          <div className="tabs">{tabs.map(([k, Icon, l]) => <button key={k} className={`tab ${cur === k ? 'active' : ''}`} onClick={() => go(k)}><Icon size={14} /> {t(l)}</button>)}</div>
          {cur === 'materials' && <Materials data={d.materials} lang={lang} t={t} reload={load} />}
          {cur === 'products' && (
            d.products.length === 0 ? <div className="card empty-state"><p>{t('no_data')}</p></div> : <>
            <OverheadCard rows={d.actual_overhead || []} t={t} />
            <div className="cs-grid">{d.products.map(p => <ProductCard key={p.id} p={p} matsById={matsById} lang={lang} t={t} canProfit={d.can_see_profit} onRecipe={() => setRecipe(p)} />)}</div>
            </>
          )}
          {cur === 'batches' && <Batches lang={lang} t={t} />}
          {cur === 'profit' && <Profit lang={lang} t={t} />}
        </>
      )}
      {recipe && <RecipeModal p={recipe} materials={d.materials} lang={lang} t={t} onClose={() => setRecipe(null)} onSaved={() => { setRecipe(null); load(); }} />}
    </div>
  );
}
